import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api, PLAN_LABELS } from "../lib/config";
import { Button } from "../components/ui/button";
import { CheckCircle2, Loader2, XCircle, Home, Download, FileText, Calendar } from "lucide-react";
import { motion } from "framer-motion";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [receipt, setReceipt] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let cancel = false;
    const MAX = 10;
    const poll = async (n) => {
      if (cancel) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          // Fetch receipt
          try {
            const { data: rcp } = await api.get(`/payments/receipt/${sessionId}`);
            if (!cancel) {
              setReceipt(rcp);
              setStatus("paid");
              launchConfetti();
            }
          } catch {
            if (!cancel) setStatus("paid");
          }
          return;
        }
        if (data.status === "expired") {
          if (!cancel) setStatus("expired");
          return;
        }
        if (n >= MAX) {
          if (!cancel) setStatus("expired");
          return;
        }
        setAttempts(n + 1);
        setTimeout(() => poll(n + 1), 2000);
      } catch (e) {
        if (!cancel) setStatus("error");
      }
    };
    poll(0);
    return () => { cancel = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-orange-50 to-pink-50 flex items-center justify-center px-6 py-12">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-xl w-full"
        data-testid="payment-success-card"
      >
        {status === "checking" && (
          <div className="bg-white rounded-2xl shadow-2xl p-12 text-center">
            <Loader2 className="w-16 h-16 text-orange-500 animate-spin mx-auto mb-6" />
            <h1 className="font-heading uppercase text-3xl text-black mb-2">Verificando pago</h1>
            <p className="text-gray-600">Espera un momento... ({attempts}/10)</p>
          </div>
        )}

        {status === "paid" && receipt && (
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
            {/* Success banner */}
            <div className="bg-gradient-to-br from-green-500 to-emerald-600 p-8 text-center text-white">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring" }}
                className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center mx-auto mb-4"
              >
                <CheckCircle2 className="w-12 h-12 text-white" />
              </motion.div>
              <h1 className="font-heading uppercase text-4xl mb-1">¡Pago exitoso!</h1>
              <p className="text-white/90">Tu cuota fue procesada correctamente</p>
            </div>

            {/* Receipt body */}
            <div className="p-8">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-gray-500 mb-4">
                <FileText className="w-3 h-3" />
                Comprobante
              </div>
              <div className="text-xs text-gray-500 mb-6">No. {receipt.receipt_number}</div>

              <div className="space-y-3 mb-6">
                <Row label="Nombre" value={receipt.registrant_name} />
                <Row label="Email" value={receipt.registrant_email} />
                <Row label="Cuota" value={`${receipt.installment_number} de ${receipt.total_installments}`} />
                <Row label="Plan" value={PLAN_LABELS[receipt.plan_type]} />
                <Row label="Fecha de pago" value={new Date(receipt.paid_at).toLocaleString("es-MX", { dateStyle: "long", timeStyle: "short" })} />
              </div>

              <div className="bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl p-5 text-black mb-6">
                <div className="text-xs uppercase tracking-widest font-bold mb-1">Cobrado</div>
                <div className="font-heading text-5xl">${receipt.amount.toFixed(2)} <span className="text-lg">{receipt.currency}</span></div>
              </div>

              {/* Progress */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs uppercase tracking-widest text-gray-500 font-bold">Tu progreso</span>
                  <span className="font-bold text-orange-600">{receipt.percentage_paid}%</span>
                </div>
                <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${receipt.percentage_paid}%` }}
                    transition={{ duration: 1.5 }}
                    className="h-full bg-gradient-to-r from-yellow-400 to-orange-500"
                  />
                </div>
                <div className="flex justify-between mt-2 text-xs text-gray-600">
                  <span>Pagado: <strong>${receipt.total_paid_so_far}</strong></span>
                  <span>Restante: <strong>${receipt.total_remaining}</strong></span>
                </div>
              </div>

              {/* Event reminder */}
              <div className="flex items-center gap-3 p-4 bg-orange-50 border border-orange-200 rounded-xl mb-6">
                <Calendar className="w-5 h-5 text-orange-600 flex-shrink-0" />
                <div className="text-sm text-gray-800">
                  Te esperamos el <strong>28 de agosto, 2026</strong> en Operación Rescate.
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={() => window.print()}
                  variant="outline"
                  data-testid="receipt-print-button"
                  className="flex-1"
                >
                  <Download className="w-4 h-4 mr-2" /> Imprimir / PDF
                </Button>
                <Button
                  onClick={() => navigate("/")}
                  data-testid="success-home-button"
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-bold uppercase tracking-wider"
                >
                  <Home className="w-4 h-4 mr-2" /> Volver al inicio
                </Button>
              </div>
              {receipt.total_remaining > 0 && (
                <Link
                  to={`/mi-cuenta/${localStorage.getItem('last_registration_id') || ''}`}
                  className="block text-center text-sm text-orange-600 hover:underline mt-4"
                >
                  Ver mi calendario de cuotas →
                </Link>
              )}
            </div>
          </div>
        )}

        {(status === "expired" || status === "error" || (status === "paid" && !receipt)) && status !== "checking" && status !== "paid" && (
          <div className="bg-white rounded-2xl shadow-2xl p-12 text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
            <h1 className="font-heading uppercase text-3xl text-black mb-2">No pudimos confirmar el pago</h1>
            <p className="text-gray-600 mb-6">Si el cobro fue exitoso, te llegará confirmación por email. Si no, intenta de nuevo.</p>
            <Button onClick={() => navigate("/")} className="bg-orange-500 hover:bg-orange-600 text-white">
              Volver al inicio
            </Button>
          </div>
        )}
      </motion.div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between items-center text-sm">
      <span className="text-gray-500 uppercase tracking-wider text-xs">{label}</span>
      <span className="text-black font-semibold text-right">{value}</span>
    </div>
  );
}

function launchConfetti() {
  const colors = ["#FFC107", "#FF4500", "#00E5FF", "#C6FF00", "#FF2D95"];
  for (let i = 0; i < 80; i++) {
    const el = document.createElement("div");
    el.className = "confetti";
    el.style.left = Math.random() * 100 + "vw";
    el.style.background = colors[Math.floor(Math.random() * colors.length)];
    el.style.animationDelay = Math.random() * 0.5 + "s";
    el.style.transform = `rotate(${Math.random() * 360}deg)`;
    document.body.appendChild(el);
    setTimeout(() => el.remove(), 3500);
  }
}
